import React, { useEffect, useState } from 'react';
import { useUserData } from '../contexts/UserDataContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

// Define the type for a favorite word
interface FavoriteWord {
    _id?: string;
    en?: string;
    word?: string;
    translation?: string;
    meaning?: string;
    difficulty?: string;
    [key: string]: any;
}

const difficultyOptions = [
    { key: 'low', label: 'Low' },
    { key: 'medium', label: 'Medium' },
    { key: 'high', label: 'High' },
];

export default function FavoriteWordsCard() {
    const { getFavoriteWords, removeFavoriteWord, userData } = useUserData();
    const { currentUser } = useAuth();
    const [selectedDifficulty, setSelectedDifficulty] = useState<string>('low');
    const [words, setWords] = useState<FavoriteWord[]>([]);
    const [loading, setLoading] = useState(false);
    const [userCount, setUserCount] = useState<number>(0);
    const PAGE_SIZE = 5;
    const [page, setPage] = useState(0);

    // Debug logs at the top
    console.log('FavoriteWordsCard - userData:', userData);
    console.log('FavoriteWordsCard - selectedLanguage:', userData?.selectedLanguage);
    console.log('FavoriteWordsCard - userCount:', userCount);
    console.log('FavoriteWordsCard - words:', words);

    useEffect(() => {
        setWords([]); // Clear old data immediately
        setLoading(true);
        getFavoriteWords(selectedDifficulty)
            .then(res => {
                setWords(res || []);
                setPage(0);
                // Debug log after API call
                console.log('FavoriteWordsCard - API words:', res);
            })
            .finally(() => setLoading(false));
        if (currentUser?.uid) {
            api.getFavoriteWordsCount(currentUser.uid, selectedDifficulty)
                .then(count => {
                    setUserCount(count);
                    // Debug log after count API call
                    console.log('FavoriteWordsCard - API userCount:', count);
                })
                .catch(() => setUserCount(0));
        }
    }, [selectedDifficulty, getFavoriteWords, currentUser?.uid]);

    const maxPage = Math.max(0, Math.ceil(words.length / PAGE_SIZE) - 1);
    const pageWords = words.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    return (
        <div className="bg-white/70 backdrop-blur-lg border border-yellow-200 rounded-2xl p-8 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="max-w-3xl mx-auto">
                <h1 className="text-4xl font-extrabold tracking-tight drop-shadow-lg mb-6 text-center">
                    <span className="bg-gradient-to-r from-yellow-500 via-pink-500 to-yellow-400 bg-clip-text text-transparent">Favorite Words</span>
                </h1>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mb-6">
                {difficultyOptions.map((opt, idx) => (
                    <button
                        key={opt.key}
                        className={`px-6 py-2 rounded-full font-semibold text-base transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300 ${selectedDifficulty === opt.key ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white scale-105' : 'bg-blue-100 text-blue-800 hover:bg-gradient-to-r hover:from-blue-200 hover:via-purple-100 hover:to-pink-100 hover:text-blue-900'}`}
                        onClick={() => setSelectedDifficulty(opt.key)}
                    >
                        {idx === 0 ? 'Beginner Level' : idx === 1 ? 'Intermediate Level' : 'Advanced Level'}
                    </button>
                ))}
            </div>
            {loading ? (
                <div className="text-center text-lg text-yellow-500 font-semibold py-12">Loading...</div>
            ) : words.length === 0 ? (
                <div className="bg-white/70 backdrop-blur-lg border border-yellow-200 rounded-2xl p-8 shadow-xl">
                    <div className="text-center">
                        <div className="text-4xl mb-4">⭐</div>
                        <p className="text-gray-600">No favorite words yet. Start learning to see them here!</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                        <div className="text-lg font-bold text-yellow-700">Total: {words.length}</div>
                    </div>
                    <div className="flex flex-col gap-4">
                        {pageWords.map((w, idx) => (
                            <div key={w._id || w.word || w.en || idx} className="bg-white/70 rounded-2xl p-4 shadow-md border border-yellow-100 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                <div>
                                    <div className="text-xl font-bold text-gray-900 mb-1">
                                        {w[userData?.selectedLanguage || 'en'] || w.word || w.en || <span className='italic text-gray-400'>Word not found</span>}
                                    </div>
                                    {userData?.selectedLanguage !== 'en' && (w.word || w.en) && (
                                        <div className="text-base text-gray-500">{w.en || w.word}</div>
                                    )}
                                    {w.meaning && (
                                        <div className="text-base text-gray-500 italic">{w.meaning}</div>
                                    )}
                                </div>
                                <button
                                    className="flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-red-400 to-red-600 text-white font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                                    onClick={async () => {
                                        await removeFavoriteWord(w.itemId);
                                        setWords(prev => prev.filter((item, i) => i !== idx));
                                    }}
                                >
                                    <span>🗑️</span> Unfavorite
                                </button>
                            </div>
                        ))}
                    </div>
                    {words.length > PAGE_SIZE && (
                        <div className="flex justify-center gap-4 mt-4">
                            <button
                                className="px-4 py-2 rounded-full bg-gray-200 text-gray-700 font-semibold disabled:opacity-50"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={page === 0}
                            >
                                Previous
                            </button>
                            <span className="px-4 py-2 text-gray-700 font-semibold">Page {page + 1} of {maxPage + 1}</span>
                            <button
                                className="px-4 py-2 rounded-full bg-blue-500 text-white font-semibold disabled:opacity-50"
                                onClick={() => setPage(p => (p + 1 < maxPage + 1 ? p + 1 : p))}
                                disabled={page === maxPage}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
} 